import React from 'react';
import { Button } from '@checkatrade/web-ui';
import {
  IconArrowRight,
  IconCheckMark,
  IconElectricalServices,
  IconLocalOffer,
} from 'lib/design-system';
import { trackEvent } from 'lib/sdks/analytics/track';

export type ShopBannerProps = {
  productName: string;
  headline: string;
  discountAmount: number;
  featureText: string;
  bulletPoints?: string[];
  ctaLabel: string;
  ctaUrl: string;
  onClick?: (e: React.MouseEvent) => void;
  productImg: string;
  productImgAlt: string;
};

export const ShopBanner: React.FC<ShopBannerProps> = ({
  productName,
  headline,
  discountAmount,
  featureText,
  bulletPoints = [],
  ctaLabel,
  ctaUrl,
  onClick,
  productImg,
  productImgAlt,
}) => {
  React.useEffect(() => {
    trackEvent('shop.banner_show', { productName, ctaUrl });
  });

  const handleClick = (e: React.MouseEvent) => {
    const url = new URL(ctaUrl);
    url.searchParams.set('utm_source', 'checkatrade');
    url.searchParams.set('utm_medium', 'shop_banner');

    if (onClick) onClick(e);
    trackEvent('shop.banner_click', { productName, ctaUrl });
    window.open(url, '_self');
  };

  return (
    <div
      className="relative mx-auto flex min-h-[56px] w-full max-w-[1050px] flex-col items-center gap-4 rounded-2xl bg-[#DFE2FF] p-4 shadow-md md:flex-row"
      role="region"
      aria-label="Shop banner"
      style={{ boxSizing: 'border-box' }}
    >
      {/* Images: badge and product, overlapped and rotated */}
      <div className="relative flex h-[56px] w-[113px] shrink-0 items-center justify-start">
        {/* Shop badge */}
        <div className="absolute left-0 top-0 z-10 flex size-[52px] -rotate-6 items-center justify-center rounded-[4.45px] bg-white p-1 shadow-sm">
          <img
            src={'/static/shop/logo.png'}
            alt={'shop with @checkatrade'}
            className="h-[24px] w-[44px] object-contain"
            aria-label="Shop with Checkatrade badge"
          />
        </div>
        {/* Product image */}
        <div className="absolute left-[60px] top-[2px] z-20 size-[52px] rotate-3 overflow-hidden rounded-[4.45px] shadow-md">
          <img
            src={productImg}
            alt={productImgAlt}
            className="size-full object-cover"
            aria-label={productImgAlt}
          />
        </div>
      </div>

      {/* Text and badges */}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        {/* Headline */}
        <span className="text-secondary leading-1 mr-2 min-w-0 break-words text-xl font-bold text-center md:text-left">
          {headline}
        </span>
        <div className="mb-0 flex min-w-0 flex-wrap items-center justify-center md:justify-start gap-2">
          {/* Discount badge */}
          <span className="bg-secondary inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-white">
            <IconLocalOffer color="white" size="small" />£{discountAmount} off
          </span>
          {/* Feature badge */}
          <span className="bg-secondary inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold text-white">
            <IconElectricalServices color="white" size="small" />
            {featureText}
          </span>
          {/* Bullet points */}
          {bulletPoints.map((point, index) => (
            <span
              key={index}
              className="text-secondary inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold"
            >
              <IconCheckMark size="small" color="#606FFF" />
              {point}
            </span>
          ))}
        </div>
      </div>

      {/* CTA Button */}
      <div className="ml-0 mt-2 flex w-full items-center md:ml-auto md:mt-0 md:w-auto">
        <Button
          className="bg-primary hover:bg-primary-700 h-[48px] min-h-[48px] w-full min-w-[110px] rounded-full px-6 py-0 text-base font-bold text-white shadow-lg md:w-auto"
          variant="primary"
          size="lg"
          onClick={handleClick}
          startContent={<IconArrowRight size="large" color="#FFFFFF" />}
          aria-label={ctaLabel}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};
